/**
 * About Page Component
 *
 * Platform overview page explaining Armenian Link's mission and services.
 * Showcases how Links, QR Codes, Pages, and Forms help Armenian organizations
 * connect with their communities.
 *
 * @module pages/about
 *
 * @remarks
 * - Mission: "Connecting Armenians, One Link At A Time"
 * - Platform overview: Links, QR Codes, Landing Pages, Forms
 * - Use cases for Armenian organizations (churches, restaurants, nonprofits, etc.)
 * - Campaign tracking explainer with real-world examples
 * - Frictionless experience: "No account required. No credit card required."
 *
 * Key Sections:
 * 1. Hero - Mission and vision for Armenian communities
 * 2. Platform Overview - All four services explained
 * 3. Use Cases - How different Armenian organizations benefit
 * 4. Campaign Tracking - Simplified explanation with examples
 * 5. CTA - "Get started free"
 *
 * @example
 * // Route definition in App.tsx
 * <Route path="/about" element={<AboutPage />} />
 */

import { useRef, useCallback, useMemo, lazy, Suspense } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, useInView } from 'motion/react';

// ⚡ Performance Optimization: Lazy load QR code library for feature examples
const LazyQRCodeCanvas = lazy(() =>
  import('qrcode.react').then((module) => ({
    default: module.QRCodeCanvas,
  }))
);
import {
  BarChart3,
  Target,
  TrendingUp,
  Users,
  Globe,
  Megaphone,
  Calendar,
  Heart,
  ArrowRight,
  Lightbulb,
  Sparkles,
  GraduationCap,
  Church,
  UtensilsCrossed,
} from 'lucide-react';
import { useReducedMotion } from '@/client/hooks/use-reduced-motion';
import {
  getEnabledServices,
  getToolsHeadline,
  getServiceNamesList,
  isServiceEnabled,
  type ServiceId,
} from '@/client/lib/config';

// =============================================================================
// Service Icon Map (derived from config)
// =============================================================================

// Map service names to their service IDs for lookup
const serviceNameToId: Record<string, ServiceId> = {
  Links: 'links',
  'Short Links': 'links',
  'QR Codes': 'qr_codes',
  QR: 'qr_codes',
  Forms: 'forms',
  Pages: 'pages',
  'Landing Pages': 'pages',
  'Email Newsletter': 'email_newsletter',
  Email: 'email_newsletter',
  'Direct Mail': 'direct_mail',
  Mail: 'direct_mail',
  'SMS Campaigns': 'sms',
  SMS: 'sms',
  'Workflow Automation': 'workflows',
  Workflows: 'workflows',
};

// Get service icon map dynamically from enabled services
function getServiceIconMap(): Record<string, { icon: React.ElementType; color: string }> {
  const enabledServices = getEnabledServices();
  const map: Record<string, { icon: React.ElementType; color: string }> = {};

  for (const service of enabledServices) {
    // Map by both name and shortName
    map[service.name] = { icon: service.icon, color: service.gradient };
    if (service.shortName !== service.name) {
      map[service.shortName] = { icon: service.icon, color: service.gradient };
    }
  }

  return map;
}

// =============================================================================
// Use Case Card Component
// =============================================================================

interface UseCaseExample {
  service:
    | 'Links'
    | 'QR Codes'
    | 'Forms'
    | 'Pages'
    | 'Email Newsletter'
    | 'Direct Mail'
    | 'SMS Campaigns'
    | 'Workflow Automation';
  text: string;
}

interface UseCaseCardProps {
  icon: React.ElementType;
  title: string;
  description: string;
  examples: UseCaseExample[];
  index: number;
}

function UseCaseCard({ icon: Icon, title, description, examples, index }: UseCaseCardProps) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });
  const prefersReducedMotion = useReducedMotion();

  // Get the icon map once
  const serviceIconMap = useMemo(() => getServiceIconMap(), []);

  // Filter examples to only show enabled services
  const filteredExamples = useMemo(() => {
    return examples.filter((example) => {
      const serviceId = serviceNameToId[example.service];
      return serviceId ? isServiceEnabled(serviceId) : false;
    });
  }, [examples]);

  // Don't render if no examples match enabled services
  if (filteredExamples.length === 0) {
    return null;
  }

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{
        delay: prefersReducedMotion ? 0 : index * 0.1,
        duration: prefersReducedMotion ? 0.01 : 0.5,
      }}
      className="relative bg-card/80 backdrop-blur-sm rounded-2xl p-8 border border-border hover:border-border/80 transition-all"
    >
      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center mb-6">
        <Icon className="w-7 h-7 text-white" />
      </div>
      <h3 className="text-xl font-semibold text-foreground mb-3">{title}</h3>
      <p className="text-muted-foreground mb-6 leading-relaxed">{description}</p>
      <div className="space-y-3">
        {filteredExamples.map((example, i) => {
          const serviceInfo = serviceIconMap[example.service];
          if (!serviceInfo) return null;
          const ServiceIcon = serviceInfo.icon;
          return (
            <div
              key={i}
              className="flex items-start gap-3 p-3 rounded-xl bg-muted/30 hover:bg-muted/50 transition-colors"
            >
              <div
                className={`w-8 h-8 rounded-lg bg-gradient-to-br ${serviceInfo.color} flex items-center justify-center shrink-0`}
              >
                <ServiceIcon className="w-4 h-4 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <span className="text-xs font-semibold text-foreground/80 uppercase tracking-wide">
                  {example.service}
                </span>
                <p className="text-sm text-muted-foreground mt-0.5">{example.text}</p>
              </div>
            </div>
          );
        })}
      </div>
    </motion.div>
  );
}

// =============================================================================
// Benefit Card Component
// =============================================================================

interface BenefitProps {
  icon: React.ElementType;
  title: string;
  description: string;
}

function Benefit({ icon: Icon, title, description }: BenefitProps) {
  return (
    <div className="flex gap-4">
      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500/20 to-orange-500/20 flex items-center justify-center flex-shrink-0">
        <Icon className="w-6 h-6 text-amber-500" />
      </div>
      <div>
        <h4 className="font-semibold text-foreground mb-1">{title}</h4>
        <p className="text-muted-foreground text-sm leading-relaxed">{description}</p>
      </div>
    </div>
  );
}

// =============================================================================
// Use Cases Data
// =============================================================================

const useCases = [
  {
    icon: Church,
    title: 'Churches & Parishes',
    description:
      'Engage your congregation and coordinate volunteers with tools built for community.',
    examples: [
      {
        service: 'Links' as const,
        text: 'Share announcements in bulletins and track who reads them',
      },
      {
        service: 'QR Codes' as const,
        text: 'Add to church signage for event info and donation pages',
      },
      {
        service: 'Forms' as const,
        text: 'Coordinate volunteers for festivals and community events',
      },
      {
        service: 'Pages' as const,
        text: 'Create event info pages for pilgrimages and celebrations',
      },
      {
        service: 'Email Newsletter' as const,
        text: 'Weekly parish updates and sermon summaries to parishioners',
      },
      {
        service: 'Direct Mail' as const,
        text: 'Send holiday cards and stewardship appeal letters',
      },
    ],
  },
  {
    icon: UtensilsCrossed,
    title: 'Restaurants & Cafes',
    description: 'Connect with customers and understand what brings them through the door.',
    examples: [
      { service: 'QR Codes' as const, text: 'Table menus that track which locations get scans' },
      { service: 'Links' as const, text: 'Social media promotion with analytics' },
      { service: 'Forms' as const, text: 'Collect reservations and catering inquiries' },
      { service: 'Pages' as const, text: 'Special event announcements and seasonal menus' },
      { service: 'Email Newsletter' as const, text: 'Weekly specials and loyalty program updates' },
      { service: 'Direct Mail' as const, text: 'Grand opening postcards to local neighborhoods' },
    ],
  },
  {
    icon: Heart,
    title: 'Nonprofits & Fundraisers',
    description: 'Maximize your outreach and understand donor behavior.',
    examples: [
      { service: 'Links' as const, text: 'Track which fundraising emails get the most clicks' },
      { service: 'Forms' as const, text: 'Event registration and volunteer coordination' },
      { service: 'Pages' as const, text: 'Campaign landing pages with donation buttons' },
      { service: 'QR Codes' as const, text: 'Print materials for galas and community events' },
      { service: 'Email Newsletter' as const, text: 'Monthly impact reports and donation appeals' },
      { service: 'Direct Mail' as const, text: 'Year-end fundraising letters and thank you cards' },
    ],
  },
  {
    icon: Calendar,
    title: 'Event Organizers',
    description: 'Promote events and manage registrations with professional tools.',
    examples: [
      { service: 'Forms' as const, text: 'Event signup with time slots (like SignUpGenius)' },
      { service: 'Links' as const, text: 'Track promotion across Instagram, email, and flyers' },
      { service: 'Pages' as const, text: 'Event info with schedule, location, and registration' },
      { service: 'QR Codes' as const, text: 'Posters and table cards that drive signups' },
      { service: 'Email Newsletter' as const, text: 'Event reminders and post-event photo recaps' },
      { service: 'Direct Mail' as const, text: 'Save-the-date cards and formal invitations' },
    ],
  },
  {
    icon: Megaphone,
    title: 'Advocacy Networks',
    description: 'Mobilize your community and track engagement on important causes.',
    examples: [
      { service: 'Links' as const, text: 'Track action alerts by congressional district' },
      { service: 'Pages' as const, text: 'Campaign microsites for commemoration events' },
      { service: 'Forms' as const, text: 'Petition signatures and volunteer signups' },
      { service: 'QR Codes' as const, text: 'Rally materials and community handouts' },
      {
        service: 'Email Newsletter' as const,
        text: 'Legislative updates and call-to-action alerts',
      },
      { service: 'Direct Mail' as const, text: 'Advocacy postcards to elected officials' },
    ],
  },
  {
    icon: GraduationCap,
    title: 'Student Organizations',
    description: 'Promote events and build institutional memory that survives transitions.',
    examples: [
      { service: 'Links' as const, text: 'Track which platforms your peers actually use' },
      { service: 'Forms' as const, text: 'Collect RSVPs for banquets and cultural shows' },
      { service: 'Pages' as const, text: 'Club info and event calendars' },
      { service: 'QR Codes' as const, text: 'Flyers for campus events and mixers' },
      { service: 'Email Newsletter' as const, text: 'Semester updates and upcoming event digests' },
      { service: 'Direct Mail' as const, text: 'Alumni reunion invitations and graduation cards' },
    ],
  },
];

// =============================================================================
// About Page
// =============================================================================

export function AboutPage() {
  const navigate = useNavigate();
  const prefersReducedMotion = useReducedMotion();

  const handleCreateFirstLink = useCallback(() => {
    // Navigate to home page and scroll to the demo section
    navigate('/');
    setTimeout(() => {
      const demoSection = document.getElementById('try-demo');
      if (demoSection) {
        demoSection.scrollIntoView({ behavior: 'smooth' });
        // Focus the URL input field for frictionless experience
        setTimeout(() => {
          const urlInput = document.querySelector(
            '#try-demo input[type="text"]'
          ) as HTMLInputElement;
          if (urlInput) {
            urlInput.focus();
          }
        }, 500);
      }
    }, 100);
  }, [navigate]);

  return (
    <div className="bg-background min-h-screen">
      {/* Decorative background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl" />
        <div className="absolute top-1/3 right-1/4 w-80 h-80 bg-orange-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 left-1/3 w-72 h-72 bg-red-500/5 rounded-full blur-3xl" />
      </div>

      {/* Hero Section - Mission */}
      <section className="relative pt-20 pb-16 lg:pt-28 lg:pb-24">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: prefersReducedMotion ? 0.01 : 0.6 }}
              className="text-center mb-12"
            >
              <p className="text-amber-500 font-medium mb-4 tracking-wide">OUR MISSION</p>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6">
                Connecting Armenians,{' '}
                <span className="bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent">
                  One Link At A Time
                </span>
              </h1>
              <p className="text-xl text-muted-foreground leading-relaxed">
                Links, QR codes, landing pages, and forms — all the tools Armenian organizations
                need to engage their communities and understand what&apos;s working.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                delay: prefersReducedMotion ? 0 : 0.2,
                duration: prefersReducedMotion ? 0.01 : 0.6,
              }}
              className="bg-card rounded-2xl p-8 md:p-10 border border-border shadow-sm"
            >
              <div className="space-y-4 text-muted-foreground leading-relaxed text-lg">
                <p>
                  Fortune 500 companies have sophisticated tools to reach their audiences — link
                  analytics, event registration, landing pages, email campaigns. They have marketing
                  teams and six-figure budgets.
                </p>
                <p>
                  Armenian churches, cultural centers, and small businesses are doing equally
                  important work with far fewer resources. The parish council coordinating
                  volunteers for a festival. The restaurant owner needing a simple way to collect
                  reservations. The nonprofit trying to understand which fundraising emails
                  resonate.
                </p>
                <p className="text-foreground/80 font-medium">
                  Professional tools shouldn&apos;t require a professional budget. That&apos;s why
                  we built Armenian Link — an integrated platform giving our community the same
                  capabilities that enterprise marketing teams take for granted.
                </p>
              </div>

              {/* Vision */}
              <div className="mt-8 pt-6 border-t border-border">
                <p className="text-muted-foreground text-sm leading-relaxed">
                  <span className="text-foreground font-medium">Our vision:</span> The Armenian
                  diaspora runs on volunteer energy — youth groups organizing dances, professional
                  networks hosting mixers, churches coordinating pilgrimages. We&apos;re building
                  tools to make that energy go further. Today it&apos;s links and forms. Tomorrow,
                  we want to help Armenian organizations coordinate, communicate, and thrive
                  together.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Platform Overview Section */}
      <section className="relative py-16 lg:py-24 border-b border-border bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: prefersReducedMotion ? 0.01 : 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              One platform,{' '}
              <span className="bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent capitalize">
                {getToolsHeadline()}
              </span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {getEnabledServices().length === 1
                ? 'Create links, track clicks, and understand your audience — all from one dashboard.'
                : getEnabledServices().length === 2
                  ? `${getServiceNamesList()} work together. Create, track, and optimize — all from one dashboard.`
                  : `Everything works together. Create a link, generate its QR code, embed it in a landing page, collect signups, and reach your audience — all from one dashboard.`}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {getEnabledServices().map((service, index) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{
                  delay: prefersReducedMotion ? 0 : index * 0.1,
                  duration: prefersReducedMotion ? 0.01 : 0.6,
                }}
                className="bg-card rounded-xl p-6 border border-border text-center relative"
              >
                {!service.available && (
                  <span className="absolute top-3 right-3 inline-flex items-center px-2 py-0.5 rounded-full bg-muted text-muted-foreground text-[10px] font-medium">
                    Coming Soon
                  </span>
                )}
                <div
                  className={`w-12 h-12 rounded-xl bg-gradient-to-br ${service.gradient} flex items-center justify-center mx-auto mb-4`}
                >
                  <service.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-semibold text-foreground mb-2">{service.name}</h3>
                <p className="text-sm text-muted-foreground">{service.description}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: prefersReducedMotion ? 0.01 : 0.6 }}
            className="text-center mt-8"
          >
            <Link
              to="/features"
              className="inline-flex items-center gap-2 text-amber-500 hover:text-amber-400 font-medium transition-colors"
            >
              Explore all features
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* The Problem Section */}
      <section className="relative py-16 lg:py-24 border-b border-border">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: prefersReducedMotion ? 0.01 : 0.6 }}
              className="grid md:grid-cols-2 gap-12 items-center"
            >
              <div>
                <h2 className="text-3xl font-bold text-foreground mb-6">
                  The problem with regular links
                </h2>
                <div className="space-y-4 text-muted-foreground">
                  <p>
                    When you share a regular link, it goes out into the world and... that&apos;s it.
                    You have no idea if anyone clicked, who they were, or where they came from.
                  </p>
                  <p>
                    You might be posting on Instagram, sending emails, printing flyers — but which
                    one actually works? Without data, you&apos;re guessing.
                  </p>
                  <p>
                    Plus, long URLs are a pain on mobile. They break across lines in text messages,
                    look messy in social bios, and are impossible to type from a flyer. Short links
                    are clean, memorable, and easy to share anywhere.
                  </p>
                  <p className="text-foreground font-medium">
                    That&apos;s like running a store and not counting who walks through the door.
                  </p>
                </div>
              </div>
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-red-500/20 to-orange-500/20 rounded-3xl blur-2xl" />
                <div className="relative bg-card/90 rounded-2xl p-6 border border-border">
                  <div className="font-mono text-sm space-y-3">
                    <p className="text-muted-foreground">Regular link:</p>
                    <p className="text-red-400 break-all text-xs">
                      https://example.com/products/summer-sale-2024-limited-edition-collection?utm_source=instagram&utm_medium=social&ref=bio
                    </p>
                    <div className="pt-4 border-t border-border">
                      <p className="text-muted-foreground">With Armenian Link:</p>
                      <p className="text-amber-400">arml.ink/summer24</p>
                    </div>
                  </div>
                  {/* QR Code Example */}
                  <div className="mt-6 pt-6 border-t border-border flex items-center gap-4">
                    <div className="bg-white p-2 rounded-lg">
                      <Suspense
                        fallback={
                          <div
                            className="bg-muted animate-pulse rounded"
                            style={{ width: 72, height: 72 }}
                          />
                        }
                      >
                        <LazyQRCodeCanvas
                          value="https://arml.ink/summer24"
                          size={72}
                          level="H"
                          marginSize={0}
                          imageSettings={{
                            src: '/assets/logo.png',
                            height: 18,
                            width: 18,
                            excavate: true,
                          }}
                        />
                      </Suspense>
                    </div>
                    <div className="flex-1">
                      <p className="text-muted-foreground text-sm">
                        Every link comes with a QR code — perfect for posters, flyers, business
                        cards, and menus.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="relative py-16 lg:py-24">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: prefersReducedMotion ? 0.01 : 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              What you learn from{' '}
              <span className="bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent">
                every click
              </span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Each click on your short link tells you something valuable about your audience.
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, x: prefersReducedMotion ? 0 : -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: prefersReducedMotion ? 0.01 : 0.6 }}
              className="space-y-6"
            >
              <Benefit
                icon={Globe}
                title="Where they are"
                description="See the countries and cities where your clicks come from. Discover if you have an audience in places you never expected."
              />
              <Benefit
                icon={Users}
                title="What they use"
                description="Know if your audience uses iPhones or Android, desktop or mobile. Create content that works for how they browse."
              />
              <Benefit
                icon={TrendingUp}
                title="When they're active"
                description="Find out what time of day and which days get the most clicks. Post when your audience is paying attention."
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: prefersReducedMotion ? 0 : 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: prefersReducedMotion ? 0.01 : 0.6 }}
              className="space-y-6"
            >
              <Benefit
                icon={Target}
                title="Where they came from"
                description="Track whether clicks came from Twitter, email, or your business card. Know which channels drive results."
              />
              <Benefit
                icon={BarChart3}
                title="What's working"
                description="Compare different links and campaigns. Double down on what works and stop wasting time on what doesn't."
              />
              <Benefit
                icon={Lightbulb}
                title="What to do next"
                description="Turn data into action. If San Francisco loves your content, maybe it's time to plan an event there."
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Campaign Tracking Explainer */}
      <section className="relative py-16 lg:py-24 border-y border-border">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: prefersReducedMotion ? 0.01 : 0.6 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Campaign tracking,{' '}
                <span className="bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent">
                  simplified
                </span>
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Stop wondering which of your marketing efforts actually work. Campaigns let you
                track clicks from different sources — all with one simple tag.
              </p>
            </motion.div>

            {/* What is a campaign? */}
            <motion.div
              initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: prefersReducedMotion ? 0.01 : 0.6 }}
              className="bg-card/80 rounded-2xl p-8 border border-border mb-8"
            >
              <h3 className="text-xl font-semibold text-foreground mb-4">What is a campaign?</h3>
              <p className="text-muted-foreground mb-6">
                A campaign is just a label you add to your link. Think of it as a name tag that
                tells you
                <span className="text-foreground font-medium"> where a click came from</span>. You
                can use the same link everywhere, but add different campaign tags to track each
                source separately.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="bg-muted/50 rounded-xl p-4">
                  <p className="text-amber-500 font-medium mb-2">Without campaigns</p>
                  <p className="text-muted-foreground text-sm">
                    &quot;We got 500 clicks this month... but where did they come from?&quot;
                  </p>
                </div>
                <div className="bg-muted/50 rounded-xl p-4">
                  <p className="text-green-500 font-medium mb-2">With campaigns</p>
                  <p className="text-muted-foreground text-sm">
                    &quot;Instagram brought 300, our flyers brought 150, and email brought 50
                    clicks.&quot;
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Real example */}
            <motion.div
              initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: prefersReducedMotion ? 0.01 : 0.6 }}
              className="bg-card/80 rounded-2xl p-8 border border-border"
            >
              <h3 className="text-lg font-semibold text-foreground mb-6">
                Real example: Restaurant Promotion
              </h3>
              <p className="text-muted-foreground text-sm mb-6">
                Let&apos;s say you&apos;re promoting your restaurant&apos;s new menu. You&apos;re
                sharing links on Instagram, printing QR codes for table flyers, and sending emails.
                Here&apos;s how campaigns help:
              </p>
              <div className="space-y-6">
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="bg-muted/50 rounded-xl p-4">
                    <p className="text-xs text-muted-foreground mb-2">Instagram Story</p>
                    <p className="font-mono text-amber-500 text-sm">arml.ink/menu</p>
                    <p className="text-xs text-muted-foreground mt-2">
                      Campaign: <span className="text-foreground">instagram-story</span>
                    </p>
                  </div>
                  <div className="bg-muted/50 rounded-xl p-4">
                    <p className="text-xs text-muted-foreground mb-2">Table Flyer QR Code</p>
                    <p className="font-mono text-amber-500 text-sm">arml.ink/menu</p>
                    <p className="text-xs text-muted-foreground mt-2">
                      Campaign: <span className="text-foreground">table-qr</span>
                    </p>
                  </div>
                  <div className="bg-muted/50 rounded-xl p-4">
                    <p className="text-xs text-muted-foreground mb-2">Email Newsletter</p>
                    <p className="font-mono text-amber-500 text-sm">arml.ink/menu</p>
                    <p className="text-xs text-muted-foreground mt-2">
                      Campaign: <span className="text-foreground">email-oct</span>
                    </p>
                  </div>
                </div>
                <div className="pt-6 border-t border-border space-y-4">
                  <p className="text-muted-foreground text-sm">
                    <span className="text-foreground font-medium">The result:</span> Your dashboard
                    shows you exactly how many people scanned the QR code at their table vs. clicked
                    from your Instagram story.
                  </p>
                  <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4">
                    <p className="text-amber-500 text-sm font-medium mb-1">Why this matters</p>
                    <p className="text-muted-foreground text-sm">
                      If table QR codes bring in 3x more visitors than Instagram, you know to invest
                      more in print materials. Stop guessing and start making decisions based on
                      real data.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="relative py-16 lg:py-24">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: prefersReducedMotion ? 0.01 : 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Built for{' '}
              <span className="bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent">
                Armenian organizations
              </span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              See how different organizations in our community use the platform to engage their
              audiences.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {useCases.map((useCase, index) => (
              <UseCaseCard key={useCase.title} {...useCase} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-16 lg:py-24 border-t border-border">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: prefersReducedMotion ? 0.01 : 0.6 }}
            >
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center mx-auto mb-6">
                <Sparkles className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Ready to connect your community?
              </h2>
              <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                Start for free — create links, generate QR codes, and see your first analytics. No
                account required for basic links. When you&apos;re ready for forms, landing pages,
                and advanced features, Plus is just $5/month.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <button
                  onClick={handleCreateFirstLink}
                  className="group inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 px-8 py-4 text-lg font-semibold text-zinc-900 shadow-lg shadow-amber-500/25 hover:shadow-amber-500/40 transition-all"
                  style={{
                    transform: 'scale(1)',
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    if (!prefersReducedMotion) {
                      e.currentTarget.style.transform = 'scale(1.05)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                >
                  Get Started Free
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
                <Link
                  to="/features"
                  className="inline-flex items-center justify-center rounded-xl border border-border bg-muted/50 hover:bg-muted px-8 py-4 text-lg font-medium text-foreground transition-all"
                >
                  Explore Features
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
